import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  ButtonIcon,
  Flex,
  MenuListBox,
  MenuListBoxItem,
  MenuTrigger,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
} from '@backstage/ui';
import {
  RiCheckboxCircleLine,
  RiGithubFill,
  RiSettings3Line,
} from '@remixicon/react';
import { useAnalytics } from '@backstage/core-plugin-api';
import { HomeCard } from '../HomeCard';
import { useGitHubSignIn } from '../useGitHubSignIn';
import { NeedsYouList } from './NeedsYouList';
import { OpenPullRequestsTab } from './OpenPullRequestsTab';
import { RefreshOnFocus, useRefreshNow } from '../useAsyncValue';
import { NEEDS_YOU_SOURCES, useNeedsYouSettings } from './settings';
import {
  useBlueprintItems,
  useCurrentUserRef,
  useDependencySecurityItems,
  useDevboxItems,
  useDeployItems,
  useInternalAppItems,
  useLockItems,
  myPullRequestItems,
  useMyPullRequests,
  useReviewRequestItems,
} from './sources';
import {
  NeedsYouItem,
  NeedsYouSourceId,
  PRIORITY_ORDER,
  URGENCY_ORDER,
} from './types';

// Panels line up with the card title; list rows run to the card edges.
const PANEL_STYLE = {
  padding: 'var(--bui-space-3) var(--bui-space-5) var(--bui-space-4)',
};

/** Blocking first, then your move, wrap-up and background; within each, the
 * stronger tone, then the oldest. */
export const sortNeedsYouItems = (items: NeedsYouItem[]) =>
  [...items].sort(
    (a, b) =>
      PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority) ||
      URGENCY_ORDER.indexOf(a.urgency) - URGENCY_ORDER.indexOf(b.urgency) ||
      (a.since ?? Infinity) - (b.since ?? Infinity),
  );

/**
 * "Needs you": things across the portal waiting for the signed-in user, most
 * urgent first. Each source is a hook in ./sources; people choose which ones
 * they see from the settings menu in the card header. Everything reloads
 * when someone comes back to the window after a while (RefreshOnFocus).
 */
export const NeedsYouCard = () => (
  <RefreshOnFocus>
    <YourWorkCard />
  </RefreshOnFocus>
);

const YourWorkCard = () => {
  const analytics = useAnalytics();
  const settings = useNeedsYouSettings();
  const github = useGitHubSignIn();
  const userRef = useCurrentUserRef();
  const refreshNow = useRefreshNow();
  const panelContentRef = useRef<HTMLDivElement>(null);
  // A Retry runs from 'requested' through 'loading' to done.
  const retry = useRef<'idle' | 'requested' | 'loading'>('idle');
  // The user's pull requests serve both tabs. They load with Needs you when
  // that source is on, otherwise when the pull requests tab is first opened.
  const [pullRequestsOpened, setPullRequestsOpened] = useState(false);
  const myPullRequests = useMyPullRequests(
    github.signedIn && (settings.isOn('github-my-prs') || pullRequestsOpened),
  );

  const sources = [
    useReviewRequestItems(settings.isOn('github-reviews'), github.signedIn),
    myPullRequestItems(
      settings.isOn('github-my-prs') && github.signedIn,
      myPullRequests,
    ),
    useDependencySecurityItems(settings.isOn('dependency-security')),
    useBlueprintItems(settings.isOn('blueprints')),
    useLockItems(settings.isOn('locks'), userRef),
    useDevboxItems(settings.isOn('devboxes'), userRef),
    useInternalAppItems(settings.isOn('internal-apps')),
    useDeployItems(settings.isOn('deployments'), github.signedIn),
  ];
  const sourceIds: NeedsYouSourceId[] = [
    'github-reviews',
    'github-my-prs',
    'dependency-security',
    'blueprints',
    'locks',
    'devboxes',
    'internal-apps',
    'deployments',
  ];

  const items = sortNeedsYouItems(sources.flatMap(source => source.items));
  const loading = sources.some(source => source.loading);
  // Skeletons only on the first load: a refresh keeps what's on screen.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!loading) setLoaded(true);
  }, [loading]);
  const failureReasons = [
    ...new Set(sources.flatMap(source => source.error?.message ?? [])),
  ];
  const failed = sources.flatMap((source, index) =>
    source.error
      ? (NEEDS_YOU_SOURCES.find(s => s.id === sourceIds[index])?.title ??
        sourceIds[index])
      : [],
  );
  // When a Retry succeeds, the alert and its focused button disappear; keep
  // keyboard and screen reader users in the card rather than on the page.
  // When it fails again, say when it tried, so it doesn't look like nothing
  // happened.
  const [retriedAt, setRetriedAt] = useState<Date>();
  useEffect(() => {
    if (retry.current === 'requested' && loading) retry.current = 'loading';
    if (retry.current !== 'loading' || loading) return;
    retry.current = 'idle';
    if (failed.length > 0) {
      setRetriedAt(new Date());
      return;
    }
    if (document.activeElement === document.body) {
      panelContentRef.current?.focus();
    }
  }, [loading, failed.length]);
  // Whatever cleared the failures, a Retry or a reload on returning to the
  // window, the next alert must not show an old "Tried again".
  useEffect(() => {
    if (failed.length === 0) setRetriedAt(undefined);
  }, [failed.length]);
  const needsGitHub =
    !github.signedIn &&
    (['github-reviews', 'github-my-prs', 'deployments'] as const).some(id =>
      settings.isOn(id),
    );

  // One view per page load once everything has loaded, with the item count.
  const viewed = useRef(false);
  useEffect(() => {
    if (loading || viewed.current) return;
    viewed.current = true;
    analytics.captureEvent('needs_you_view', 'needs-you', {
      value: items.length,
    });
  }, [analytics, loading, items.length]);

  const onAction = (item: NeedsYouItem) =>
    analytics.captureEvent('needs_you_click', item.label, {
      attributes: { source: item.source, category: item.urgency },
    });
  const onPullRequestAction = (item: NeedsYouItem) =>
    analytics.captureEvent('needs_you_click', 'Open pull request', {
      attributes: { source: 'open-pull-requests', category: item.urgency },
    });

  const settingsMenu = (
    <MenuTrigger>
      <ButtonIcon
        size="small"
        variant="tertiary"
        icon={<RiSettings3Line />}
        aria-label="Choose what Needs you shows"
      />
      <MenuListBox
        selectionMode="multiple"
        // Escape closes the menu; by default it would also clear every
        // selection, which here means hiding every source.
        escapeKeyBehavior="none"
        aria-label="Sources"
        selectedKeys={settings.available
          .filter(source => !settings.hidden.includes(source.id))
          .map(source => source.id)}
        onSelectionChange={keys => {
          const on = keys === 'all' ? null : new Set(keys);
          const hidden = settings.available
            .filter(source => on && !on.has(source.id))
            .map(source => source.id);
          for (const source of settings.available) {
            const wasOn = !settings.hidden.includes(source.id);
            const isOn = !hidden.includes(source.id);
            if (wasOn !== isOn) {
              analytics.captureEvent('needs_you_source_toggle', source.id, {
                attributes: { status: isOn ? 'on' : 'off' },
              });
            }
          }
          settings.setHidden(hidden);
        }}
      >
        {settings.available.map(source => (
          <MenuListBoxItem key={source.id} id={source.id}>
            {source.title}
          </MenuListBoxItem>
        ))}
      </MenuListBox>
    </MenuTrigger>
  );

  // The badge counts every row, so it matches the list below it.
  const countBadge =
    items.length > 0 ? (
      <Text
        as="span"
        variant="body-small"
        weight="bold"
        aria-label={`${items.length} items`}
        style={{
          marginLeft: 'var(--bui-space-1_5)',
          padding: '0 var(--bui-space-2)',
          borderRadius: 'var(--bui-radius-full)',
          backgroundColor: 'var(--make-bg-brand-subtle)',
          color: 'var(--make-fg-brand)',
        }}
      >
        {items.length}
      </Text>
    ) : null;

  const githubSignIn = (
    <Flex
      align="center"
      gap="3"
      p="3"
      style={{
        borderRadius: 'var(--bui-radius-3)',
        backgroundColor: 'var(--bui-bg-neutral-2)',
      }}
    >
      <RiGithubFill size={20} aria-hidden style={{ flexShrink: 0 }} />
      <Text variant="body-small" color="secondary" style={{ flex: 1 }}>
        Sign in to GitHub to see reviews and your pull requests.
      </Text>
      <Button size="small" variant="secondary" onPress={github.signIn}>
        Sign in
      </Button>
    </Flex>
  );

  return (
    <HomeCard title="Your work" flush>
      <Tabs
        defaultSelectedKey="needs-you"
        onSelectionChange={key => {
          if (key === 'pull-requests') setPullRequestsOpened(true);
        }}
      >
        {/* The tab list starts at the Tabs root's top left corner: BUI
        places the active underline and the hover highlight from there.
        The divider is on this wrapper so the underline sits on it. */}
        <div style={{ borderBottom: '1px solid var(--bui-border-1)' }}>
          <TabList
            aria-label="Your work"
            style={{ paddingLeft: 'var(--bui-space-3)' }}
          >
            <Tab id="needs-you">Needs you{countBadge}</Tab>
            <Tab id="pull-requests">Open pull requests</Tab>
          </TabList>
        </div>
        <TabPanel id="needs-you" style={PANEL_STYLE}>
          {/* Focus lands here after a successful Retry, whose button goes
          away with the alert. */}
          <div ref={panelContentRef} tabIndex={-1} style={{ outline: 'none' }}>
            <Flex direction="column" gap="3">
              {/* The settings menu lives in the panel: react-aria renders
            everything else inside Tabs twice to collect the tabs, and a
            menu's list box breaks in that extra pass. */}
              <Flex align="center" justify="between" gap="2">
                <Text variant="body-small" color="secondary">
                  Most urgent first
                </Text>
                {settingsMenu}
              </Flex>
              {needsGitHub && githubSignIn}
              {/* Above the list, so a failed source can't pass for "nothing
            needs you". */}
              {failed.length > 0 && (
                <Alert
                  status="warning"
                  icon
                  title={`Couldn't load: ${failed.join(', ')}.`}
                  // Why, so a report from someone can name the cause.
                  description={[
                    ...failureReasons,
                    retriedAt &&
                      `Tried again at ${retriedAt.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  customActions={
                    <Button
                      size="small"
                      variant="secondary"
                      isPending={loading}
                      onPress={() => {
                        retry.current = 'requested';
                        refreshNow();
                      }}
                    >
                      Retry
                    </Button>
                  }
                />
              )}

              {loading && !loaded && items.length === 0 ? (
                <Flex
                  direction="column"
                  gap="2"
                  role="progressbar"
                  aria-label="Loading"
                >
                  {[0, 1, 2].map(i => (
                    <Skeleton key={i} width="100%" height={48} rounded />
                  ))}
                </Flex>
              ) : null}

              {/* "Nothing needs you" only when every source answered. */}
              {(loaded || !loading) &&
                items.length === 0 &&
                failed.length === 0 && (
                  <Flex align="center" gap="2" py="4" justify="center">
                    <RiCheckboxCircleLine
                      size={20}
                      aria-hidden
                      style={{ color: 'var(--bui-fg-success)' }}
                    />
                    <Text variant="body-medium" color="secondary">
                      Nothing needs you right now.
                    </Text>
                  </Flex>
                )}

              {items.length > 0 && (
                <NeedsYouList
                  label="Things that need you"
                  items={items}
                  onAction={onAction}
                />
              )}
            </Flex>
          </div>
        </TabPanel>
        {/* The pull requests load when this tab is first selected. */}
        <TabPanel id="pull-requests" style={PANEL_STYLE}>
          <Flex direction="column" gap="3">
            {github.signedIn ? (
              <OpenPullRequestsTab
                prs={myPullRequests}
                onAction={onPullRequestAction}
              />
            ) : (
              githubSignIn
            )}
          </Flex>
        </TabPanel>
      </Tabs>
    </HomeCard>
  );
};
